import * as THREE from 'three';


// Soglie snap mobile→parete (drag da catalogo)
const FURN_SNAP_ACQUIRE = 0.2;   // 20 cm – entra nello snap
const FURN_SNAP_RELEASE = 0.35;  // 35 cm – esce dallo snap (isteresi)
const WALL_THICKNESS    = 0.2;

class DragDropManager {
  constructor(camera, domElement, furnitureController, wallController, doorController, windowController) {
    this.camera = camera;
    this.domElement = domElement;
    this.furnitureController = furnitureController;
    this.wallController = wallController;
    this.doorController = doorController;
    this.windowController = windowController;

    this.raycaster   = new THREE.Raycaster();
    this.mouse       = new THREE.Vector2();
    this.groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

    this.isDragging      = false;
    this.draggedItemId   = null;
    this.draggedItemType = null;
    this.previewMesh     = null;

    // Margine minimo tra oggetti (10 cm)
    this.minimumSpacing = 0.1;

    // Stato snap
    this._snapMarkers     = [];   // array di mesh (max 2)
    this._snapSceneAdded  = false;
    this._snapActive      = false;
    // Isteresi snap
    this._ddSnapped       = false;
    this._ddSnapWall      = null;
    this._ddSnapNormal    = null;
    this._createSnapIndicator();

    // Ghost element per touch drag
    this._touchGhost = null;

    // Bind mouse
    this.onDragOver  = this.onDragOver.bind(this);
    this.onDrop      = this.onDrop.bind(this);
    this.onDragLeave = this.onDragLeave.bind(this);

    // Bind touch (globali, attivati durante il drag)
    this._onTouchMove = this._onTouchMove.bind(this);
    this._onTouchEnd  = this._onTouchEnd.bind(this);

    this.setupEventListeners();
  }

  // SNAP – sfere visive (max 2, una per parete agganciata)
  _makeOneMarker() {
    const sGeo = new THREE.SphereGeometry(0.12, 16, 16);
    const sMat = new THREE.MeshBasicMaterial({
      color: 0xffcc00, transparent: true, opacity: 0.95, depthTest: false
    });
    const marker = new THREE.Mesh(sGeo, sMat);
    marker.renderOrder = 999;
    marker.visible = false;
    const rGeo = new THREE.RingGeometry(0.16, 0.26, 32);
    const rMat = new THREE.MeshBasicMaterial({
      color: 0xffcc00, transparent: true, opacity: 0.5,
      side: THREE.DoubleSide, depthTest: false
    });
    const ring = new THREE.Mesh(rGeo, rMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = -0.11;
    marker.add(ring);
    return marker;
  }

  _createSnapIndicator() {
    this._snapMarkers = [this._makeOneMarker(), this._makeOneMarker()];
  }

  _ensureSnapInScene() {
    if (this._snapSceneAdded) return;
    const scene = this.furnitureController.scene;
    if (!scene) return;
    this._snapMarkers.forEach(m => scene.add(m));
    this._snapSceneAdded = true;
  }

  _showSnapMarkers(points) {
    this._ensureSnapInScene();
    this._snapMarkers.forEach((m, i) => {
      if (i < points.length) {
        m.position.set(points[i].x, 0.12, points[i].z);
        m.visible = true;
      } else {
        m.visible = false;
      }
    });
    this._snapActive = true;
    window.requestRender?.();
  }

  _hideSnapMarkers() {
    this._snapMarkers.forEach(m => { if (m) m.visible = false; });
    this._snapActive = false;
    window.requestRender?.();
  }

  // GEOMETRIA SNAP
  _closestPointOnSeg(p, a, b) {
    const px = p.x, pz = p.z;
    const ax = a.x, az = a.z;
    const dx = b.x - ax, dz = b.z - az;
    const len2 = dx * dx + dz * dz;
    let t = 0;
    if (len2 > 0) t = Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / len2));
    const cx = ax + t * dx;
    const cz = az + t * dz;
    return {
      point: new THREE.Vector3(cx, 0, cz),
      dist:  Math.sqrt((px - cx) ** 2 + (pz - cz) ** 2)
    };
  }

  _obbHalfExtent(normal, rotY, width, depth) {
    const fwdX  =  Math.sin(rotY);
    const fwdZ  =  Math.cos(rotY);
    const rgtX  =  Math.cos(rotY);
    const rgtZ  = -Math.sin(rotY);
    return Math.abs(normal.x * fwdX + normal.z * fwdZ) * (depth  / 2)
         + Math.abs(normal.x * rgtX + normal.z * rgtZ) * (width  / 2);
  }

  _collectWallSnapCandidates(pos, rotY, width, depth, radius) {
    const walls = this.wallController.getAllWalls();
    const candidates = [];

    for (const w of walls) {
      const cp = this._closestPointOnSeg(pos, w.start, w.end);

      const toObj = new THREE.Vector3(pos.x - cp.point.x, 0, pos.z - cp.point.z);
      const wallAngle = w.getAngle();
      const nx0 = -Math.sin(wallAngle);
      const nz0 =  Math.cos(wallAngle);
      const normal = new THREE.Vector3(nx0, 0, nz0);
      if (toObj.dot(normal) < 0) normal.negate();

      const halfExtent  = this._obbHalfExtent(normal, rotY, width, depth);
      const wallHalf    = (w.thickness ?? WALL_THICKNESS) / 2;
      const surfaceDist = cp.dist - halfExtent - wallHalf;

      if (surfaceDist < radius) {
        candidates.push({ point: cp.point.clone(), wall: w, normal: normal.clone(),
                          halfExtent, wallHalf, surfaceDist });
      }
    }
    candidates.sort((a, b) => a.surfaceDist - b.surfaceDist);
    return candidates;
  }

  _resolveWallSnap(basePos, candidates) {
    if (!candidates.length) return null;

    const a = candidates[0];

    if (candidates.length === 1) {
      const ws = new THREE.Vector3(a.wall.start.x, 0, a.wall.start.z);
      const we = new THREE.Vector3(a.wall.end.x,   0, a.wall.end.z);
      const wd = new THREE.Vector3().subVectors(we, ws).normalize();
      const wl = ws.distanceTo(we);
      const bf = new THREE.Vector3(basePos.x, 0, basePos.z);
      const t  = Math.max(0, Math.min(wl, new THREE.Vector3().subVectors(bf, ws).dot(wd)));
      const contactOffset = a.halfExtent + a.wallHalf;
      const snappedPos = ws.clone().add(wd.clone().multiplyScalar(t))
                           .add(a.normal.clone().multiplyScalar(contactOffset));
      snappedPos.y = 0;
      return { snappedPos, primarySnap: a, multiSnap: false };
    }

    const b   = candidates[1];
    const ka  = a.point.dot(a.normal) + a.halfExtent + a.wallHalf;
    const kb  = b.point.dot(b.normal) + b.halfExtent + b.wallHalf;
    const det = a.normal.x * b.normal.z - a.normal.z * b.normal.x;

    if (Math.abs(det) < 0.05) return this._resolveWallSnap(basePos, [a]);

    const px = (ka * b.normal.z - kb * a.normal.z) / det;
    const pz = (a.normal.x * kb - b.normal.x * ka) / det;
    return { snappedPos: new THREE.Vector3(px, 0, pz), primarySnap: a, secondarySnap: b, multiSnap: true };
  }

  // EVENT HANDLERS
  setupEventListeners() {
    this.domElement.addEventListener('dragover',  this.onDragOver);
    this.domElement.addEventListener('drop',      this.onDrop);
    this.domElement.addEventListener('dragleave', this.onDragLeave);
  }

  setDraggedItem(itemId) {
    this.draggedItemId = itemId;
  }

  onDragOver(event) {
    event.preventDefault();
    if (this.isLocked) {
      event.dataTransfer.dropEffect = 'none';
      return;
    }
    event.dataTransfer.dropEffect = 'copy';

    const itemId = this.draggedItemId || event.dataTransfer.getData('itemId');
    if (!itemId) return;

    const item = this.furnitureController.getItemById(itemId);
    if (!item) return;

    const position = this.getGroundIntersection(event);
    if (!position) return;

    if (!this.isDragging || this.draggedItemId !== itemId) {
      this.draggedItemId   = itemId;
      this.draggedItemType = item.type;
      this.isDragging      = true;
      if (item.type === 'door')   this.doorController.enablePlacementMode(item);
      if (item.type === 'window') this.windowController.enablePlacementMode(item);
    }

    this._updatePreviewPosition(position, event, item, itemId);
  }

  onDrop(event) {
    event.preventDefault();
    if (this.isLocked) { this.cleanup(); return; }

    const itemId = this.draggedItemId || event.dataTransfer.getData('itemId');
    if (!itemId) { this.cleanup(); return; }

    const item = this.furnitureController.getItemById(itemId);
    if (!item)  { this.cleanup(); return; }

    const position = this.getGroundIntersection(event);
    if (!position) { this.cleanup(); return; }

    this._dropAtPosition(item, itemId, position);
  }

  onDragLeave(event) {
    const rect = this.domElement.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right ||
        event.clientY < rect.top  || event.clientY > rect.bottom) {
      this.cleanup();
    }
  }

  async createFurniture(itemId, position) {
    try {
      const furniture = await this.furnitureController.createFurniture(itemId, position);
      if (furniture) {
        console.log('✔ Mobile posizionato:', furniture.catalogItem.name);
        this.showPlacementFeedback(position);
        window.requestRender?.();
      }
    } catch (error) {
      console.error('Errore posizionamento mobile:', error);
    }
  }

  createPreview(itemId) {
    this.removePreview();
    const item = this.furnitureController.getItemById(itemId);
    if (!item) return;

    const geometry = new THREE.BoxGeometry(
      item.dimensions.width, item.dimensions.height, item.dimensions.depth
    );
    const material = new THREE.MeshBasicMaterial({
      color: 0x00cc00, transparent: true, opacity: 0.3, wireframe: false
    });
    this.previewMesh = new THREE.Mesh(geometry, material);
    this.previewMesh.position.y = item.dimensions.height / 2;

    const edgeMat = new THREE.LineBasicMaterial({ color: 0x00cc00, linewidth: 2 });
    const edgeLines = new THREE.LineSegments(new THREE.EdgesGeometry(geometry), edgeMat);
    this.previewMesh.add(edgeLines);

    this.furnitureController.scene.add(this.previewMesh);
    window.requestRender?.();
  }

  removePreview() {
    if (this.previewMesh) {
      this.furnitureController.scene.remove(this.previewMesh);
      this.previewMesh.traverse(c => {
        if (c.geometry) c.geometry.dispose();
        if (c.material)  c.material.dispose();
      });
      this.previewMesh = null;
      window.requestRender?.();
    }
  }

  updatePreviewValidity(position, item) {
    if (!this.previewMesh || !item) return;
    const isValid = this.checkValidPlacement(position, item);
    const col = isValid ? 0x00cc00 : 0xff0000;
    this.previewMesh.material.color.setHex(col);
    if (this.previewMesh.children[0]) this.previewMesh.children[0].material.color.setHex(col);
  }

  checkValidPlacement(position, item) {
    const dims   = item.dimensions;
    const margin = this.minimumSpacing;
    const box    = new THREE.Box3();
    box.min.set(position.x - dims.width/2 - margin, -margin,              position.z - dims.depth/2 - margin);
    box.max.set(position.x + dims.width/2 + margin, dims.height + margin,  position.z + dims.depth/2 + margin);

    for (const f of this.furnitureController.getAllFurniture()) {
      const b = f.getBoundingBox();
      if (b && box.intersectsBox(b)) return false;
    }
    for (const d of this.doorController.getAllDoors()) {
      if (d.mesh && box.intersectsBox(new THREE.Box3().setFromObject(d.mesh))) return false;
    }
    for (const w of this.windowController.getAllWindows()) {
      if (w.mesh && box.intersectsBox(new THREE.Box3().setFromObject(w.mesh))) return false;
    }
    return true;
  }

  findNearestWall(point) {
    const walls = this.wallController.getAllWalls();
    if (!walls.length) return { wall: null, position: null, distance: Infinity };

    let nearest = null, minDist = Infinity, closest = null;
    walls.forEach(w => {
      const r = this.getClosestPointOnWall(point, w);
      if (r.distance < minDist) { minDist = r.distance; nearest = w; closest = r.point; }
    });
    return nearest ? { wall: nearest, position: closest, distance: minDist }
                   : { wall: null, position: null, distance: Infinity };
  }

  getClosestPointOnWall(point, wall) {
    const p  = new THREE.Vector3(point.x, 0, point.z);
    const a  = new THREE.Vector3(wall.start.x, 0, wall.start.z);
    const b  = new THREE.Vector3(wall.end.x,   0, wall.end.z);
    const ab = new THREE.Vector3().subVectors(b, a);
    const len = ab.length(); ab.normalize();
    const t  = Math.max(0, Math.min(len, new THREE.Vector3().subVectors(p, a).dot(ab)));
    const cp = a.clone().add(ab.clone().multiplyScalar(t));
    return { point: cp, distance: p.distanceTo(cp) };
  }

  snapToGrid(position, gridSize = 0.5) {
    return new THREE.Vector3(
      Math.round(position.x / gridSize) * gridSize,
      position.y,
      Math.round(position.z / gridSize) * gridSize
    );
  }

  showPlacementFeedback(position) {
    const geometry = new THREE.CircleGeometry(0.5, 32);
    const material = new THREE.MeshBasicMaterial({ color: 0x00cc00, transparent: true, opacity: 0.7 });
    const circle   = new THREE.Mesh(geometry, material);
    circle.rotation.x = -Math.PI / 2;
    circle.position.copy(position);
    circle.position.y = 0.01;

    const scene = this.furnitureController.scene;
    scene.add(circle);

    let opacity = 0.7;
    const fade = setInterval(() => {
      opacity -= 0.05;
      circle.material.opacity = opacity;
      window.requestRender?.();
      if (opacity <= 0) {
        clearInterval(fade);
        scene.remove(circle);
        circle.geometry.dispose();
        circle.material.dispose();
        window.requestRender?.();
      }
    }, 30);
  }

  // TOUCH DRAG
  startTouchDrag(itemId, touchStartEvent) {
    if (this.isLocked) return;
    const item = this.furnitureController.getItemById(itemId);
    if (!item) return;

    this.draggedItemId   = itemId;
    this.draggedItemType = item.type;
    this.isDragging      = true;
    this._touchLeftCatalog = false;

    document.addEventListener('touchmove',   this._onTouchMove, { passive: false });
    document.addEventListener('touchend',    this._onTouchEnd,  { passive: false });
    document.addEventListener('touchcancel', this._onTouchEnd,  { passive: false });
  }

  _createTouchGhost(sourceEl, touchEvent) {
    this._removeTouchGhost();

    const ghost = document.createElement('div');
    ghost.id = 'touch-drag-ghost';

    const thumbEl = sourceEl?.querySelector('.item-thumbnail');
    if (thumbEl) {
      const thumbClone = thumbEl.cloneNode(true);
      thumbClone.querySelector('.delete-model-button')?.remove();
      thumbClone.style.cssText = `
        width: 72px;
        height: 72px;
        border-radius: 8px;
        overflow: hidden;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        margin: 0;
      `;
      ghost.appendChild(thumbClone);
    }

    ghost.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 99999;
      opacity: 0.9;
      transform: translate(-50%, -100%);
    `;

    const t = touchEvent.changedTouches[0];
    ghost.style.left = t.clientX + 'px';
    ghost.style.top  = t.clientY + 'px';

    document.body.appendChild(ghost);
    this._touchGhost = ghost;
  }

  _removeTouchGhost() {
    if (this._touchGhost) { this._touchGhost.remove(); this._touchGhost = null; }
  }

  _onTouchMove(event) {
    event.preventDefault();
    const touch = event.changedTouches[0];

    if (!this._touchLeftCatalog) {
      const catalogPanel = document.getElementById('catalog-panel');
      if (catalogPanel) {
        const rect = catalogPanel.getBoundingClientRect();
        const overPanel = touch.clientX >= rect.left && touch.clientX <= rect.right &&
                          touch.clientY >= rect.top  && touch.clientY <= rect.bottom;
        if (overPanel) return;
      }

      this._touchLeftCatalog = true;

      const item = this.furnitureController.getItemById(this.draggedItemId);
      if (!item) return;

      const sourceEl = document.querySelector(
        `.catalog-item[data-item-id="${this.draggedItemId}"]`
      );
      this._createTouchGhost(sourceEl, event);

      if (item.type === 'door')        this.doorController.enablePlacementMode(item);
      else if (item.type === 'window') this.windowController.enablePlacementMode(item);
      else                             this.createPreview(this.draggedItemId);
    }

    if (this._touchGhost) {
      this._touchGhost.style.left = touch.clientX + 'px';
      this._touchGhost.style.top  = touch.clientY + 'px';
    }

    const item = this.furnitureController.getItemById(this.draggedItemId);
    if (!item) return;

    const position = this._getGroundFromClient(touch.clientX, touch.clientY);
    if (!position) return;

    // Crea un evento sintetico con le coordinate touch in modo che
    // doorController/windowController possano mostrare l'etichetta misure
    const synthEvent = { clientX: touch.clientX, clientY: touch.clientY, target: this.domElement };
    this._updatePreviewPosition(position, synthEvent, item, this.draggedItemId);
  }

  _onTouchEnd(event) {
    const touch = event.changedTouches[0];
    this._removeTouchGhost();
    document.removeEventListener('touchmove',   this._onTouchMove);
    document.removeEventListener('touchend',    this._onTouchEnd);
    document.removeEventListener('touchcancel', this._onTouchEnd);

    if (this.isLocked || !this._isTouchOverCanvas(touch)) { this.cleanup(); return; }

    const item = this.furnitureController.getItemById(this.draggedItemId);
    if (!item) { this.cleanup(); return; }
    const position = this._getGroundFromClient(touch.clientX, touch.clientY);
    if (!position) { this.cleanup(); return; }

    this._dropAtPosition(item, this.draggedItemId, position);
  }

  _isTouchOverCanvas(touch) {
    const rect = this.domElement.getBoundingClientRect();
    return touch.clientX >= rect.left && touch.clientX <= rect.right &&
           touch.clientY >= rect.top  && touch.clientY <= rect.bottom;
  }

  _getGroundFromClient(clientX, clientY) {
    const rect = this.domElement.getBoundingClientRect();
    this.mouse.x =  ((clientX - rect.left) / rect.width)  * 2 - 1;
    this.mouse.y = -((clientY - rect.top)  / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const pt = new THREE.Vector3();
    return this.raycaster.ray.intersectPlane(this.groundPlane, pt);
  }

  // Logica preview condivisa tra mouse e touch
  _updatePreviewPosition(position, mouseEvent, item, itemId) {
    if (item.type === 'door') {
      this.doorController.updatePreview(position, mouseEvent, this.camera);
      window.requestRender?.();
    } else if (item.type === 'window') {
      this.windowController.updatePreview(position, mouseEvent, this.camera);
      window.requestRender?.();
    } else {
      if (!this.previewMesh || this.draggedItemId !== itemId) this.createPreview(itemId);
      if (!this.previewMesh) return;

      let finalPos    = position.clone();
      let snapApplied = false;
      const previewRotY  = -(this.previewMesh.rotation?.y ?? 0);
      const detectRadius = this._ddSnapped ? FURN_SNAP_RELEASE : FURN_SNAP_ACQUIRE;
      const candidates   = this._collectWallSnapCandidates(position, previewRotY, item.dimensions.width, item.dimensions.depth, detectRadius);
      const resolved     = this._resolveWallSnap(position, candidates);

      if (resolved) {
        this._ddSnapped    = true;
        this._ddSnapWall   = resolved.primarySnap.wall;
        this._ddSnapNormal = resolved.primarySnap.normal;
        finalPos = resolved.snappedPos.clone(); finalPos.y = 0;

        const markerPoints = candidates.slice(0, 2).map(snap => {
          const ws = new THREE.Vector3(snap.wall.start.x, 0, snap.wall.start.z);
          const we = new THREE.Vector3(snap.wall.end.x,   0, snap.wall.end.z);
          const wd = new THREE.Vector3().subVectors(we, ws).normalize();
          const wl = ws.distanceTo(we);
          const pf = new THREE.Vector3(position.x, 0, position.z);
          const t  = Math.max(0, Math.min(wl, new THREE.Vector3().subVectors(pf, ws).dot(wd)));
          return ws.clone().add(wd.clone().multiplyScalar(t));
        });
        this._showSnapMarkers(markerPoints);
        snapApplied = true;
      }

      if (!snapApplied) {
        this._ddSnapped = false; this._ddSnapWall = null; this._ddSnapNormal = null;
        this._hideSnapMarkers();
        if (item.snapToGrid) finalPos = this.snapToGrid(finalPos);
        if (item.snapToWall) {
          const wr = this.findNearestWall(finalPos);
          if (wr.wall && wr.distance < 0.5) {
            finalPos = wr.position;
            this.previewMesh.rotation.y = wr.wall.getAngle();
          }
        }
      }

      this.previewMesh.position.copy(finalPos);
      this.previewMesh.position.y = item.dimensions.height / 2;
      this.updatePreviewValidity(finalPos, item);

      // Notifica l'engine che la scena è cambiata (preview si è spostata)
      window.requestRender?.();
    }
  }

  // Logica drop condivisa tra mouse e touch
  _dropAtPosition(item, itemId, position) {
    if (item.type === 'door') {
      this.doorController.placeDoor(position); this.cleanup();
    } else if (item.type === 'window') {
      this.windowController.placeWindow(position); this.cleanup();
    } else {
      let finalPos = position.clone();
      const dropRotY  = -(this.previewMesh?.rotation?.y ?? 0);
      const dropCands = this._collectWallSnapCandidates(position, dropRotY, item.dimensions.width, item.dimensions.depth, FURN_SNAP_ACQUIRE);
      const resolved  = this._resolveWallSnap(position, dropCands);
      if (resolved) { finalPos = resolved.snappedPos.clone(); finalPos.y = 0; }
      else if (item.snapToGrid) { finalPos = this.snapToGrid(finalPos); }
      else if (item.snapToWall) {
        const wr = this.findNearestWall(finalPos);
        if (wr.wall && wr.distance < 0.5) finalPos = wr.position;
      }
      if (!this.checkValidPlacement(finalPos, item)) {
        console.warn('Posizione non valida: spazio insufficiente (minimo 10 cm)');
        this.cleanup(); return;
      }
      this.createFurniture(itemId, finalPos);
      this.cleanup();
    }
  }

  getGroundIntersection(event) {
    const rect = this.domElement.getBoundingClientRect();
    this.mouse.x =  ((event.clientX - rect.left) / rect.width)  * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top)  / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const pt = new THREE.Vector3();
    return this.raycaster.ray.intersectPlane(this.groundPlane, pt);
  }

  cleanup() {
    this._hideSnapMarkers();
    this._ddSnapped        = false;
    this._ddSnapWall       = null;
    this._ddSnapNormal     = null;
    this._touchLeftCatalog = false;
    this._removeTouchGhost();
    document.removeEventListener('touchmove',   this._onTouchMove);
    document.removeEventListener('touchend',    this._onTouchEnd);
    document.removeEventListener('touchcancel', this._onTouchEnd);
    if (this.draggedItemType === 'door')   this.doorController.disablePlacementMode();
    if (this.draggedItemType === 'window') this.windowController.disablePlacementMode();
    this.removePreview();
    this.isDragging      = false;
    this.draggedItemId   = null;
    this.draggedItemType = null;
    window.requestRender?.();
  }

  dispose() {
    this.cleanup();
    if (this._snapSceneAdded && this.furnitureController?.scene) {
      const s = this.furnitureController.scene;
      this._snapMarkers.forEach(m => {
        s.remove(m);
        m.geometry.dispose();
        m.material.dispose();
      });
    }
    this.domElement.removeEventListener('dragover',  this.onDragOver);
    this.domElement.removeEventListener('drop',      this.onDrop);
    this.domElement.removeEventListener('dragleave', this.onDragLeave);
  }
}

export default DragDropManager;